import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axiosInstance from '../services/axiosInstance';

/**
 * LeetCodeIntegrationModal
 *
 * Props:
 *  open          – boolean controlling Dialog visibility
 *  onClose       – called when the modal should close
 *  initialData   – optional { session, csrfToken, lastUpdated, status }
 *                  pre-populated from a previous save
 *  onSaveSuccess – optional callback(updatedData) after a successful save
 */
const LeetCodeIntegrationModal = ({
  open,
  onClose,
  initialData = {},
  onSaveSuccess,
}) => {
  const [session, setSession] = useState(initialData.session ?? '');
  const [csrfToken, setCsrfToken] = useState(initialData.csrfToken ?? '');
  const [status] = useState(initialData.status ?? 'Not Connected');
  const [lastUpdated] = useState(initialData.lastUpdated ?? '—');

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const resetFeedback = () => {
    setError('');
    setSuccessMsg('');
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    resetFeedback();
    if (!session.trim() || !csrfToken.trim()) {
      setError('Both LEETCODE_SESSION and CSRFTOKEN are required.');
      return;
    }

    setSaving(true);
    try {
      const payload = { session: session.trim(), csrfToken: csrfToken.trim() };
      const response = await axiosInstance.post(
        '/admin/platforms/leetcode',
        payload
      );

      const now = new Date().toLocaleString();

      if (onSaveSuccess) {
        onSaveSuccess({
          session: session.trim(),
          csrfToken: csrfToken.trim(),
          lastUpdated: now,
          status: 'Connected',
          ...response.data,
        });
      }

      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Failed to save configuration. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Test Connection ──────────────────────────────────────────────────────
  const handleTestConnection = async () => {
    resetFeedback();
    if (!session.trim() || !csrfToken.trim()) {
      setError('Enter both LEETCODE_SESSION and CSRFTOKEN before testing.');
      return;
    }

    setTesting(true);
    try {
      await axiosInstance.post('/admin/platforms/leetcode/test', {
        session: session.trim(),
        csrfToken: csrfToken.trim(),
      });
      setSuccessMsg('Connection test passed.');
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Connection test failed. Check your credentials and try again.'
      );
    } finally {
      setTesting(false);
      onClose()
    }
  };

  // ── Test Email ────────────────────────────────────────────────────────────
  const handleTestEmail = async () => {
    resetFeedback();
    setTestingEmail(true);
    try {
      const { data } = await axiosInstance.post('/admin/platforms/leetcode/test-email');
      setSuccessMsg(data.message || 'Test email sent successfully.');
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Failed to send test email. Check SMTP configuration.'
      );
    } finally {
      setTestingEmail(false);
      onClose()
    }
  };

  // ── Close ─────────────────────────────────────────────────────────────────
  const handleClose = () => {
    resetFeedback();
    onClose();
  };

  const isConnected = status === 'Connected';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      {/* ── Title ── */}
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon fontSize="small" />
          <Typography variant="h6" component="span">
            LeetCode Integration
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 3 }}>
        {/* Status row */}
        <Stack
          direction="row"
          spacing={3}
          sx={{
            mb: 3,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Connection Status
            </Typography>
            <Chip
              icon={
                isConnected ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <CancelIcon fontSize="small" />
                )
              }
              label={status}
              size="small"
              color={isConnected ? 'success' : 'default'}
              variant={isConnected ? 'filled' : 'outlined'}
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Last Updated
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {lastUpdated}
            </Typography>
          </Box>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Paste the cookie values from an active LeetCode browser session. These
          are used to authenticate API requests on behalf of the platform account.
        </Typography>

        {/* Feedback alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={resetFeedback}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={resetFeedback}>
            {successMsg}
          </Alert>
        )}

        {/* LEETCODE_SESSION */}
        <TextField
          label="LEETCODE_SESSION"
          placeholder="Paste your LEETCODE_SESSION cookie value"
          value={session}
          onChange={(e) => setSession(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={3}
          sx={{ mb: 2, fontFamily: 'monospace' }}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          helperText='Found in browser DevTools → Application → Cookies → "LEETCODE_SESSION"'
        />

        {/* CSRFTOKEN */}
        <TextField
          label="CSRFTOKEN"
          placeholder="Paste your csrftoken cookie value"
          value={csrfToken}
          onChange={(e) => setCsrfToken(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          helperText='Found in browser DevTools → Application → Cookies → "csrftoken"'
        />
      </DialogContent>

      <Divider />

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="text" onClick={handleClose} disabled={saving || testing || testingEmail}>
          Cancel
        </Button>

        <Button
          variant="outlined"
          onClick={handleTestEmail}
          disabled={saving || testing || testingEmail}
          startIcon={testingEmail ? <CircularProgress size={14} /> : <EmailIcon fontSize="small" />}
        >
          {testingEmail ? 'Sending…' : 'Test Email'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleTestConnection}
          disabled={saving || testing || testingEmail}
          startIcon={testing ? <CircularProgress size={14} /> : null}
        >
          {testing ? 'Testing…' : 'Test Connection'}
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || testing || testingEmail}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeetCodeIntegrationModal;
